import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3V3 } from '../contexts/Web3ContextV3';
import toast from 'react-hot-toast';

/**
 * V3 Draw Results Page - Black & White Premium Theme
 * Latest Draw + History + Winner Statistics
 */
const DrawResultsV3 = () => {
  const { t } = useTranslation();
  const {
    contracts,
    nftStats,
    getDrawResult
  } = useWeb3V3();

  // State management
  const [loading, setLoading] = useState(true);
  const [latestDraw, setLatestDraw] = useState(null);
  const [drawHistory, setDrawHistory] = useState([]);
  const [selectedDraw, setSelectedDraw] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Load draw data
  useEffect(() => {
    if (contracts.prizePool) {
      loadDrawData();
    }
  }, [contracts.prizePool]);

  const loadDrawData = async () => {
    try {
      setLoading(true);

      const currentDrawId = parseInt(nftStats.currentDrawId || '1');

      // Load latest draw
      if (currentDrawId > 0) {
        const latest = await getDrawResult(currentDrawId);
        setLatestDraw(latest);

        // Load history (last 10 draws)
        const history = [];
        const startId = Math.max(1, currentDrawId - 9);

        for (let i = currentDrawId; i >= startId; i--) {
          try {
            const draw = await getDrawResult(i);
            if (draw && draw.finalized) {
              history.push(draw);
            }
          } catch (error) {
            // This draw may not have been finalized yet
            continue;
          }
        }

        setDrawHistory(history);
      }

    } catch (error) {
      console.error('Failed to load draw data:', error);
      toast.error(t('draws.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // View details
  const handleViewDetail = (draw) => {
    setSelectedDraw(draw);
    setShowDetailModal(true);
  };

  // Calculate total winners
  const getTotalWinners = (draw) => {
    if (!draw) return 0;
    return (
      parseInt(draw.grandWinnerCount || '0') +
      parseInt(draw.firstWinnerCount || '0') +
      parseInt(draw.secondWinnerCount || '0') +
      parseInt(draw.thirdWinnerCount || '0')
    );
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === '0') return '-';
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="container-v3">
        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-5xl font-black mb-2">{t('draws.title')}</h1>
          <p className="text-gray-400">
            {t('draws.subtitle')}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="text-gray-500 text-xl">{t('common.loading')}</div>
          </div>
        ) : (
          <>
            {/* Latest draw */}
            {latestDraw && latestDraw.finalized && (
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">{t('draws.latest')}</h2>

                <div className="card-glow p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left side: Winning number */}
                    <div className="text-center">
                      <div className="text-sm text-gray-500 uppercase tracking-wider mb-4">
                        {t('draws.period')} {latestDraw.drawId}
                      </div>
                      <div className="mb-6">
                        <div className="text-8xl font-black font-mono tracking-wider mb-4">
                          {latestDraw.winningNumber}
                        </div>
                        <div className="text-gray-500">{t('draws.winningNumber')}</div>
                      </div>
                      <div className="text-sm text-gray-400">
                        {t('draws.time')}: {formatDate(latestDraw.timestamp)}
                      </div>
                    </div>

                    {/* Right side: Winner statistics */}
                    <div>
                      <div className="mb-6">
                        <div className="text-lg font-bold mb-4">{t('draws.totalPrizePool')}</div>
                        <div className="text-4xl font-bold font-mono">
                          {parseFloat(latestDraw.totalPrizePool).toFixed(2)}{' '}
                          <span className="text-xl text-gray-500">USDT</span>
                        </div>
                      </div>

                      <div className="divider"></div>

                      {/* Prize level winners */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-950 border border-gray-800">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl"></span>
                            <span className="font-bold">{t('draws.prizes.grand')}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-mono font-bold">{latestDraw.grandWinnerCount}{t('draws.people')}</div>
                            <div className="text-xs text-gray-500">
                              {parseFloat(latestDraw.grandPrizePool).toFixed(2)} USDT
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-950 border border-gray-800">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl"></span>
                            <span className="font-bold">{t('draws.prizes.first')}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-mono font-bold">{latestDraw.firstWinnerCount}{t('draws.people')}</div>
                            <div className="text-xs text-gray-500">
                              {parseFloat(latestDraw.firstPrizePool).toFixed(2)} USDT
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-950 border border-gray-800">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl"></span>
                            <span className="font-bold">{t('draws.prizes.second')}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-mono font-bold">{latestDraw.secondWinnerCount}{t('draws.people')}</div>
                            <div className="text-xs text-gray-500">
                              {parseFloat(latestDraw.secondPrizePool).toFixed(2)} USDT
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-950 border border-gray-800">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl"></span>
                            <span className="font-bold">{t('draws.prizes.third')}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-mono font-bold">{latestDraw.thirdWinnerCount}{t('draws.people')}</div>
                            <div className="text-xs text-gray-500">
                              {parseFloat(latestDraw.thirdPrizePool).toFixed(2)} USDT
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 p-4 bg-white text-black">
                        <div className="text-sm font-bold">{t('draws.totalWinners')}</div>
                        <div className="text-3xl font-black font-mono">{getTotalWinners(latestDraw)}{t('draws.people')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* {t('draws.history')} */}
            <div>
              <h2 className="text-3xl font-bold mb-6">{t('draws.history')}</h2>

              {drawHistory.length === 0 ? (
                <div className="text-center py-20">
                  <h3 className="text-2xl font-bold mb-4">{t('draws.noData')}</h3>
                  <p className="text-gray-400">{t('draws.emptyDesc')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {drawHistory.map((draw) => (
                    <div key={draw.drawId} className="card p-6">
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        {/* Draw ID and number */}
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <div className="text-sm text-gray-500 mb-1">{t('draws.drawId')}</div>
                            <div className="text-2xl font-bold font-mono">#{draw.drawId}</div>
                          </div>
                          <div className="h-16 w-px bg-gray-800"></div>
                          <div className="text-center">
                            <div className="text-sm text-gray-500 mb-1">{t('draws.winningNumber')}</div>
                            <div className="text-3xl font-black font-mono tracking-wider">
                              {draw.winningNumber}
                            </div>
                          </div>
                        </div>

                        {/* Statistics */}
                        <div className="flex items-center gap-8">
                          <div className="text-center">
                            <div className="text-sm text-gray-500 mb-1">{t('draws.prizePool')}</div>
                            <div className="text-xl font-bold font-mono">
                              {parseFloat(draw.totalPrizePool).toFixed(0)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm text-gray-500 mb-1">{t('draws.winners')}</div>
                            <div className="text-xl font-bold font-mono">
                              {getTotalWinners(draw)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm text-gray-500 mb-1">{t('draws.time')}</div>
                            <div className="text-sm">
                              {formatDate(draw.timestamp)}
                            </div>
                          </div>
                        </div>

                        {/* View details button */}
                        <button
                          onClick={() => handleViewDetail(draw)}
                          className="btn-outline px-6 py-2 whitespace-nowrap"
                        >
                          {t('draws.viewDetails')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rules explanation */}
            <div className="mt-12">
              <div className="card p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">{t('draws.rules.title')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 border border-gray-800">
                    <div className="font-bold mb-1">{t('draws.prizes.grand')}</div>
                    <div className="text-2xl font-mono text-white mb-2">40%</div>
                    <div className="text-xs text-gray-500">{t('draws.rules.match6')}</div>
                  </div>
                  <div className="text-center p-4 border border-gray-800">
                    <div className="font-bold mb-1">{t('draws.prizes.first')}</div>
                    <div className="text-2xl font-mono text-white mb-2">30%</div>
                    <div className="text-xs text-gray-500">{t('draws.rules.match5')}</div>
                  </div>
                  <div className="text-center p-4 border border-gray-800">
                    <div className="font-bold mb-1">{t('draws.prizes.second')}</div>
                    <div className="text-2xl font-mono text-white mb-2">20%</div>
                    <div className="text-xs text-gray-500">{t('draws.rules.match4')}</div>
                  </div>
                  <div className="text-center p-4 border border-gray-800">
                    <div className="font-bold mb-1">{t('draws.prizes.third')}</div>
                    <div className="text-2xl font-mono text-white mb-2">10%</div>
                    <div className="text-xs text-gray-500">{t('draws.rules.match3')}</div>
                  </div>
                </div>
                <div className="text-center mt-6 text-sm text-gray-500">
                  {t('draws.rules.instruction')}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* {t('draws.details')}Modal */}
      {showDetailModal && selectedDraw && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="card max-w-2xl w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-3xl font-bold">{t('draws.period')} {selectedDraw.drawId} {t('draws.details')}</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-3xl hover:text-gray-400 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* {t('draws.winningNumber')} */}
              <div className="text-center p-6 bg-gray-950 border-2 border-white">
                <div className="text-sm text-gray-500 mb-2">{t('draws.winningNumber')}</div>
                <div className="text-7xl font-black font-mono tracking-wider">
                  {selectedDraw.winningNumber}
                </div>
              </div>

              {/* Prize pool info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-950 border border-gray-800">
                  <div className="text-sm text-gray-500 mb-1">{t('draws.totalPrizePool')}</div>
                  <div className="text-2xl font-bold font-mono">
                    {parseFloat(selectedDraw.totalPrizePool).toFixed(2)} USDT
                  </div>
                </div>
                <div className="p-4 bg-gray-950 border border-gray-800">
                  <div className="text-sm text-gray-500 mb-1">{t('draws.totalWinners')}</div>
                  <div className="text-2xl font-bold font-mono">
                    {getTotalWinners(selectedDraw)}{t('draws.people')}
                  </div>
                </div>
              </div>

              {/* Prize level details */}
              <div className="space-y-3">
                {[
                  { level: 4, name: t('draws.prizes.grand'), icon: '', count: selectedDraw.grandWinnerCount, pool: selectedDraw.grandPrizePool },
                  { level: 3, name: t('draws.prizes.first'), icon: '', count: selectedDraw.firstWinnerCount, pool: selectedDraw.firstPrizePool },
                  { level: 2, name: t('draws.prizes.second'), icon: '', count: selectedDraw.secondWinnerCount, pool: selectedDraw.secondPrizePool },
                  { level: 1, name: t('draws.prizes.third'), icon: '', count: selectedDraw.thirdWinnerCount, pool: selectedDraw.thirdPrizePool }
                ].map((prize) => (
                  <div key={prize.level} className="p-4 bg-gray-950 border border-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{prize.icon}</span>
                        <div>
                          <div className="font-bold text-lg">{prize.name}</div>
                          <div className="text-sm text-gray-500">{prize.count} {t('draws.people')}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{t('draws.prizePool')}</div>
                        <div className="text-xl font-bold font-mono">
                          {parseFloat(prize.pool).toFixed(2)}
                        </div>
                        {parseInt(prize.count) > 0 && (
                          <div className="text-xs text-gray-600 mt-1">
                           Avg: {(parseFloat(prize.pool) / parseInt(prize.count)).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* {t('draws.time')} */}
              <div className="text-center text-sm text-gray-500">
                {t('draws.time')}: {formatDate(selectedDraw.timestamp)}
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-outline w-full py-3"
              >
                {t('draws.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawResultsV3;
